import { useLingui } from '@lingui/react/macro';
import { animated, easings, useInView, useSpring } from '@react-spring/web';
import { BitcoinConvert, Building, ShieldTick } from 'iconsax-reactjs';
import type { FC } from 'react';
import { cn } from '@/shared/react';

export const AboutUs: FC<{ className?: string }> = ({ className }) => {
  const { t } = useLingui();
  const [ref, inView] = useInView();

  const heading = useSpring({
    opacity: inView ? 1 : 0,
    y: inView ? 0 : 30,
    delay: 100,
    config: { easing: easings.easeInBounce },
  });

  const card = useSpring({
    opacity: inView ? 1 : 0,
    y: inView ? 0 : 40,
    delay: 300,
    config: { easing: easings.easeInBounce },
  });

  const featureItems = [
    {
      key: 'licensed',
      icon: <ShieldTick size={28} variant="Bold" />,
      title: t`Secure`,
      description: t`Fully safe, prioritizing the security of your assets and data.`,
    },
    {
      key: 'fiat',
      icon: <BitcoinConvert size={28} variant="Bold" />,
      title: t`Fiat On/Off Ramp`,
      description: t`Secure, embedded fiat rails making it simple to move between IQD and digital assets.`,
    },
    {
      key: 'infrastructure',
      icon: <Building size={28} variant="Bold" />,
      title: t`Built for Iraq`,
      description: t`Building the infrastructure for the future of finance in Iraq.`,
    },
  ];

  return (
    <section id="about-us" className={cn('relative', className)}>
      <div className="px-3 mx-auto max-w-desktop" ref={ref}>
        <div className="bg-background-brand/5 border border-border-disabled rounded-5xl p-6 desktop:p-12 overflow-hidden">
          <div className="text-center mb-8 desktop:mb-12">
            <animated.p
              style={heading}
              className="text-content-brand text-sm desktop:text-base font-semibold uppercase tracking-widest mb-3"
            >
              {t`About Us`}
            </animated.p>
            <animated.h2
              style={heading}
              className="text-2xl desktop:text-4xl leading-tight mb-3"
            >
              {t`The First Crypto Exchange in Iraq`}
            </animated.h2>
            <animated.p
              style={heading}
              className="text-lg desktop:text-xl text-content-secondary max-w-3xl mx-auto mb-5"
            >
              {t`Bridging Local Finance with Global Crypto Markets`}
            </animated.p>
            <animated.p
              style={heading}
              className="text-base text-content-tertiary max-w-2xl mx-auto"
            >
              {t`IQX is the first cryptocurrency exchange in Iraq. We provide secure, embedded on/off-ramp fiat rails, making it safe and simple to move between IQD and digital assets. We are building the infrastructure for the future of finance in Iraq.`}
            </animated.p>
          </div>

          <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-6">
            {featureItems.map((item) => (
              <animated.div
                key={item.key}
                style={card}
                className="flex-1 bg-background-primary/60 border border-border-disabled rounded-3xl p-5 desktop:p-6"
              >
                <div className="size-12 rounded-2xl bg-background-brand/10 text-content-brand flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-base text-content-secondary">
                  {item.description}
                </p>
              </animated.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
